import {
    ArrowDownCircle, ArrowRightLeft, ArrowUpCircle, CheckCircle2, Clock, DollarSign, History, RefreshCw, TrendingUp
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cabinetPath } from '../lib/domains';
import { CommissionSummaryTable } from '../components/commissions/CommissionSummaryTable';
import { MerchantAdvanceCard } from '../components/MerchantAdvanceCard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { useMerchantPaymentPolling } from '../hooks/useMerchantPaymentPolling';
import {
    merchantAPI, RequisiteType, REQUISITE_TYPE_OPTIONS
} from '../lib/api';
import { getMerchantAvailableBalance } from '../lib/merchantAmounts';
import { merchantEvents, MERCHANT_EVENTS } from '../lib/merchantEvents';
import { resolveMerchantFiatCurrency } from '../lib/typicalV2';
import { formatAmount } from '../lib/utils';

import { MerchantRecentPayments } from '../components/MerchantRecentPayments';
const WITHDRAW_METHODS = new Set<RequisiteType>(['card', 'sbp', 'sbp_kgs', 'mobile_kgs']);

const methodLabel = (method: RequisiteType) =>
    REQUISITE_TYPE_OPTIONS.find((item) => item.value === method)?.label || method;

interface PaymentStats {
    total_payments: number;
    completed_payments: number;
    pending_payments: number;
    total_amount: number;
    success_rate: number;
    exchange_rate?: number | null;
    merchant_rate_source?: string | null;
    merchant_rate_markup_percent?: number | null;
}

export const MerchantDashboardPage: React.FC = () => {
    const { user, refreshUser } = useMerchantAuth();
    const isTypicalIntegration = user?.integration_profile === 'typical_v2_2';
    useMerchantPaymentPolling(!!user && !isTypicalIntegration);
    const [loading, setLoading] = useState(false);
    const [statsError, setStatsError] = useState<string | null>(null);
    const requestId = useRef(0);
    const [stats, setStats] = useState<PaymentStats>({
        total_payments: 0,
        completed_payments: 0,
        pending_payments: 0,
        total_amount: 0,
        success_rate: 0,
        exchange_rate: null,
    });
    const formatAppealTime = (minutes: number): string => {
        if (minutes < 60) {
            return `${minutes} мин`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (remainingMinutes === 0) {
            return hours === 1 ? '1 час' : `${hours} часа`;
        }
        return `${hours}ч ${remainingMinutes}м`;
    };

    const loadPaymentStats = useCallback(async (showLoading = true) => {
        if (!user?.api_key || isTypicalIntegration) return;
        const currentRequest = ++requestId.current;
        if (showLoading) setLoading(true);
        setStatsError(null);
        try {
            const data = await merchantAPI.getStats();
            if (currentRequest === requestId.current) setStats(data);
        } catch {
            if (currentRequest === requestId.current) setStatsError('Не удалось обновить статистику платежей');
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [user, isTypicalIntegration]);

    useEffect(() => {
        if (!user || isTypicalIntegration) return;
        loadPaymentStats();
        const refresh = () => { void loadPaymentStats(false); };
        const interval = setInterval(refresh, 30000);
        merchantEvents.on(MERCHANT_EVENTS.STATS_UPDATED, refresh);
        merchantEvents.on(MERCHANT_EVENTS.PAYMENT_STATUS_CHANGED, refresh);
        return () => {
            requestId.current += 1;
            clearInterval(interval);
            merchantEvents.off(MERCHANT_EVENTS.STATS_UPDATED, refresh);
            merchantEvents.off(MERCHANT_EVENTS.PAYMENT_STATUS_CHANGED, refresh);
        };
    }, [user, isTypicalIntegration, loadPaymentStats]);

    if (!user) return null;
    const merchantExchangeRate = stats.exchange_rate || user.exchange_rate || null;
    const fiatCurrency = resolveMerchantFiatCurrency(user);
    const rate = merchantExchangeRate || 0;
    const formatRub = (amount: number) => rate > 0
        ? `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${fiatCurrency === 'RUB' ? '₽' : fiatCurrency}`
        : 'Курс недоступен';
    const depositBalanceUsdt = user.deposit_balance || 0;
    const withdrawBalanceUsdt = user.withdraw_balance || 0;
    const availableBalanceUsdt = getMerchantAvailableBalance(user);
    const depositBalanceRub = depositBalanceUsdt * rate;
    const withdrawBalanceRub = withdrawBalanceUsdt * rate;
    const availableBalanceRub = availableBalanceUsdt * rate;
    const depositMethodTiers = user.deposit_commission_tiers_by_requisite_type || {};
    const withdrawMethodTiers = user.withdraw_commission_tiers_by_requisite_type || {};
    const hasDynamicDepositCommission = Boolean(
        user.deposit_commission_tiers?.length
        || Object.values(depositMethodTiers).some((tiers) => Boolean(tiers?.length))
    );
    const hasDynamicWithdrawCommission = Boolean(
        user.withdraw_commission_tiers?.length
        || Object.values(withdrawMethodTiers).some((tiers) => Boolean(tiers?.length))
    );
    const merchantMethods = user.requisite_types || (user.integration_profile === 'typical_v2_2' ? [] : ['card', 'sbp']);
    const withdrawMethods = merchantMethods.filter((method) => WITHDRAW_METHODS.has(method));
    const depositCommissionRate = (user.deposit_commission_rate || 0) / 100;
    const withdrawCommissionRate = (user.withdraw_commission_rate || 0) / 100;
    const depositGrossRub = depositCommissionRate < 1
        ? depositBalanceRub / (1 - depositCommissionRate)
        : depositBalanceRub;
    const withdrawGrossRub = withdrawCommissionRate > -1
        ? withdrawBalanceRub / (1 + withdrawCommissionRate)
        : withdrawBalanceRub;
    const depositCommissionRub = hasDynamicDepositCommission
        ? 0
        : Math.max(0, depositGrossRub - depositBalanceRub);
    const withdrawCommissionRub = hasDynamicWithdrawCommission
        ? 0
        : Math.max(0, withdrawBalanceRub - withdrawGrossRub);

    return <div className="space-y-6">
        {/* Заголовок со статистикой */}
        <div className="flex items-center justify-between ">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Обзор</h2>
            <Button
                variant="outline"
                size="sm"
                onClick={() => { void loadPaymentStats(true); void refreshUser(); }}
                disabled={loading}
            >
                {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                    <RefreshCw className="h-4 w-4" />
                )}
                Обновить
            </Button>
        </div>

        {statsError && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{statsError}</p>}
        {/* Баланс и курс */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Текущий баланс с учётом комиссий и проведённых расчётов */}
            <Card>
                <CardContent className="p-6 pt-8">
                    <div className="flex items-center mb-2">
                        <div className={`p-2 rounded-lg mr-3 ${availableBalanceUsdt >= 0 ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-red-100 dark:bg-red-950'}`}>
                            <DollarSign className={`h-6 w-6 ${availableBalanceUsdt >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`} />
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Текущий баланс</p>
                    </div>
                    <MerchantAdvanceCard initialAdvance={user.advance}>
                        <p className={`text-2xl font-bold ${availableBalanceUsdt >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'} mb-1`}>
                            {formatRub(availableBalanceRub)}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                            {availableBalanceUsdt.toFixed(2).replace('.', ',')} USDT
                        </p>
                    </MerchantAdvanceCard>
                    <Button asChild variant="outline" size="sm" className="mt-3">
                        <Link to={cabinetPath('merchant', '/topups-history')}>История пополнений</Link>
                    </Button>
                </CardContent>
            </Card>

            {/* Пополнения */}
            <Card>
                <CardContent className="p-6 pt-8">
                    <div className="flex items-center mb-2">
                        <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg mr-3">
                            <ArrowDownCircle className="h-6 w-6 text-green-600 dark:text-green-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Пополнения</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-300 mb-1">
                        {formatRub(depositGrossRub)}
                    </p>
                    <p className="text-xs text-gray-400">без учета комиссии</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        Реальный баланс: {formatRub(depositBalanceRub)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        {depositBalanceUsdt.toFixed(2).replace('.', ',')} USDT
                    </p>
                    {depositCommissionRub > 0 && (
                        <p className="text-xs text-gray-400">
                            Комиссия: {formatRub(depositCommissionRub)}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Выводы */}
            <Card>
                <CardContent className="p-6 pt-8">
                    <div className="flex items-center mb-2">
                        <div className="p-2 bg-orange-100 dark:bg-orange-950 rounded-lg mr-3">
                            <ArrowUpCircle className="h-6 w-6 text-orange-600 dark:text-orange-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Выводы</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-300 mb-1">
                        {formatRub(withdrawGrossRub)}
                    </p>
                    <p className="text-xs text-gray-400">без учета комиссии</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        Реальный баланс: {formatRub(withdrawBalanceRub)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        {withdrawBalanceUsdt.toFixed(2).replace('.', ',')} USDT
                    </p>
                    {withdrawCommissionRub > 0 && (
                        <p className="text-xs text-gray-400">
                            Комиссия: {formatRub(withdrawCommissionRub)}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Курс */}
            <Card>
                <CardContent className="p-6 pt-8">
                    <div className="flex items-center mb-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg mr-3">
                            <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Курс {fiatCurrency} / USDT</p>
                    </div>
                    {merchantExchangeRate ? (
                        <p className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-1">
                            {merchantExchangeRate.toFixed(2)} {fiatCurrency}
                        </p>
                    ) : (
                        <p className="text-lg text-red-500 mb-1">Недоступен</p>
                    )}
                    <p className="text-xs text-gray-400">за 1 USDT</p>
                </CardContent>
            </Card>
        </div>

        {!isTypicalIntegration && <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg mr-3">
                            <History className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Всего платежей</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{stats.total_payments}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg mr-3">
                            <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Завершено</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{stats.completed_payments}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-950 rounded-lg mr-3">
                            <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-slate-300">В обработке</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{stats.pending_payments}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 dark:bg-purple-950 rounded-lg mr-3">
                            <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Успешность</p>
                            <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{stats.success_rate}%</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>}

        {/* Комиссии мерчанта */}
        <Card className="">
            <CardHeader>
                <CardTitle className="text-lg">Комиссии и условия</CardTitle>
                <CardDescription>Ваши комиссии за операции ввода и вывода</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    <CommissionSummaryTable
                        currency={fiatCurrency}
                        depositRate={user.deposit_commission_rate}
                        withdrawRate={user.withdraw_commission_rate}
                        depositTiers={user.deposit_commission_tiers}
                        withdrawTiers={user.withdraw_commission_tiers}
                        depositMethodTiers={depositMethodTiers}
                        withdrawMethodTiers={withdrawMethodTiers}
                        depositMethods={merchantMethods}
                        withdrawMethods={withdrawMethods}
                        methodLabel={(method) => methodLabel(method as RequisiteType)}
                    />
                    {(user.deposit_min_amount != null || user.withdraw_min_amount != null) && (
                        <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
                            {user.deposit_min_amount != null && user.deposit_max_amount != null && (
                                <span>
                                    Пополнение: <b>{formatAmount(user.deposit_min_amount, fiatCurrency)}–{formatAmount(user.deposit_max_amount, fiatCurrency)}</b>
                                </span>
                            )}
                            {user.withdraw_min_amount != null && user.withdraw_max_amount != null && (
                                <span>
                                    Вывод: <b>{formatAmount(user.withdraw_min_amount, fiatCurrency)}–{formatAmount(user.withdraw_max_amount, fiatCurrency)}</b>
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center justify-between rounded-lg bg-purple-50 dark:bg-purple-950 p-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Время на аппеляцию:</span>
                        <Badge variant="secondary" className="text-base">
                            {formatAppealTime(user.appeal_lifetime_minutes)}
                        </Badge>
                    </div>
                </div>
            </CardContent>
        </Card>

        <MerchantRecentPayments user={user} />
    </div>;
};
